"use client";

// ============================================================
// CLUBHUB – Die Tabelle der Zuordnungen
// ============================================================
// Gefiltert und sortiert hat schon der Server (Adresse); hier
// liegen nur die gezeigten Zeilen, damit Bearbeiten sofort wirkt.
// Ein Filterwechsel lädt die Tabelle neu (`key` an der Seite) —
// im Musterbestand gehen lokale Änderungen dabei verloren. Mit
// Sitzung zeichnet der Server nach dem Speichern neu, dann gilt sein
// Stand (auch für einen Wechsel, der eine Zeile beendet).
// ============================================================
import { useState } from "react";
import Link from "next/link";
import { AlertTriangle, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Filterloesen,
  LISTENFUSS,
  Listenkoerper,
  Listentabelle,
  SortableHead,
  SortableHeaderRow,
  Textzelle,
  UEBER_DER_ZEILE,
} from "@/components/liste";
import type { SortState } from "@/lib/sorting";
import { ContactStatusBadge, TonBadge } from "@/components/mitglieder/status-badge";
import { MandatsLageBadge } from "./mandat-lage";
import { ZuordnungDialog, type MandatInfo } from "./zuordnung-dialog";
import { FEE_INTERVAL, PAYMENT_METHOD } from "@/lib/labels";
import { Z_PARAMS, Z_SORT, type ZSortKey } from "@/lib/beitraege/ansicht";
import { anzahl, euro, summe } from "@/lib/rechnungen/format";
import { jahresbetrag } from "@/lib/beitraege/regeln";
import type { BeitragsartView, ZuordnungView } from "@/lib/beitraege/views";
import type { ContactView, HouseholdView } from "@/lib/views";
import { cn, formatDate } from "@/lib/utils";

export function ZuordnungenTabelle({
  start,
  arten,
  mandate,
  sort,
  personen,
  datenbank = false,
}: {
  start: ZuordnungView[];
  arten: BeitragsartView[];
  mandate: Record<string, MandatInfo>;
  sort: SortState<ZSortKey>;
  /** Personen und Familien für den Dialog — `null` im Musterbestand. */
  personen?: { kontakte: ContactView[]; haushalte: HouseholdView[] } | null;
  datenbank?: boolean;
}) {
  const [zeilen, setZeilen] = useState(start);
  const [dialog, setDialog] = useState<ZuordnungView | "neu" | null>(null);
  const [basis, setBasis] = useState(start);
  if (basis !== start) {
    setBasis(start);
    setZeilen(start);
  }

  function gespeichert(z: ZuordnungView) {
    setZeilen((zs) => (zs.some((x) => x.id === z.id) ? zs.map((x) => (x.id === z.id ? z : x)) : [z, ...zs]));
  }

  // Familien zählen einmal: der Familienbeitrag steht an jedem Mitglied.
  const jahr = summe(
    zeilen.filter((z, i, alle) => !(z.householdId && arten.find((a) => a.id === z.feeTypeId)?.perHousehold && alle.findIndex((y) => y.householdId === z.householdId && y.feeTypeId === z.feeTypeId) !== i)),
    (z) => jahresbetrag(z.amount, z.interval),
  );

  return (
    <>
      <div className="border-border-subtle flex items-center gap-3 border-b px-4 py-2">
        <p className="text-caption text-text-tertiary">Ein Familienbeitrag steht an jedem Mitglied der Familie, berechnet wird er einmal an die Zahlperson.</p>
        <Button size="sm" variant="outline" className="ml-auto shrink-0" onClick={() => setDialog("neu")}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Zuordnen
        </Button>
      </div>

      {zeilen.length === 0 ? (
        <Listenkoerper className="p-5">
          <EmptyState icon={Users} title="Keine Zuordnung passt zu diesem Filter" description="Ändere die Suche oder setze die Filter zurück." action={<Filterloesen params={Z_PARAMS} />} />
        </Listenkoerper>
      ) : (
        <>
          <Listentabelle viewportClassName="max-md:hidden">
            <TableHeader>
              <SortableHeaderRow sort={sort} defaultColumn="name">
                <SortableHead column="name" {...Z_SORT.name}>Person</SortableHead>
                <SortableHead column="beitrag" {...Z_SORT.beitrag}>Beitragsart</SortableHead>
                <SortableHead column="betrag" {...Z_SORT.betrag} align="right">Betrag</SortableHead>
                <TableHead>Zahlt</TableHead>
                <TableHead>Zahlart</TableHead>
                <SortableHead column="seit" {...Z_SORT.seit}>Gültig</SortableHead>
                <TableHead>Hinweis</TableHead>
              </SortableHeaderRow>
            </TableHeader>
            <TableBody>
              {zeilen.map((z) => (
                <TableRow key={z.id} className={cn("border-border-subtle relative cursor-pointer border-b", z.luecken.length > 0 && "[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-warning)]")}>
                  <Textzelle
                    text={
                      <button type="button" onClick={() => setDialog(z)} className="block min-w-0 text-left after:absolute after:inset-0 after:content-['']">
                        <span className="hover:text-accent block truncate font-medium">{z.contactName}</span>
                        <span className="text-caption text-text-tertiary block truncate tabular-nums">
                          {z.memberNumber ? `Nr. ${z.memberNumber}` : "ohne Nummer"}
                          {z.alter !== null ? ` · ${z.alter} Jahre` : ""}
                        </span>
                      </button>
                    }
                  />
                  <TableCell className="whitespace-nowrap">
                    {z.feeTypeName}
                    <span className="text-caption text-text-tertiary block">{FEE_INTERVAL[z.interval]}</span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap tabular-nums">
                    <span className="font-medium">{z.amount === 0 ? "frei" : euro(z.amount)}</span>
                    {z.amountOverride !== null && <span className="text-caption text-text-tertiary block line-through">{euro(z.baseAmount)}</span>}
                  </TableCell>
                  <TableCell className="max-w-48">
                    {z.payerId === z.contactId ? (
                      <span className="text-text-secondary">selbst</span>
                    ) : (
                      <span className="block truncate">
                        <Link href={`/mitglieder/${z.payerId}`} className={cn(UEBER_DER_ZEILE, "hover:text-accent")}>
                          {z.payerName}
                        </Link>
                        <span className="text-caption text-text-tertiary block truncate">{z.householdName}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {PAYMENT_METHOD[z.paymentMethod]}
                    {z.paymentMethod === "SEPA" && (
                      <span className="mt-0.5 flex items-center gap-1.5">
                        {z.mandateReference && <span className="text-caption text-text-tertiary tabular-nums">{z.mandateReference}</span>}
                        {z.mandatsLage && z.mandatsLage !== "gueltig" && <MandatsLageBadge lage={z.mandatsLage} />}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    ab {formatDate(z.validFrom)}
                    {z.validUntil && <span className="text-caption text-text-tertiary block">bis {formatDate(z.validUntil)}</span>}
                  </TableCell>
                  <TableCell className="max-w-72">
                    <Hinweise z={z} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <tfoot>
              <tr>
                <td colSpan={2} className={LISTENFUSS}>
                  <span className="text-body-sm font-medium">Im Jahr</span>
                  <span className="text-caption text-text-tertiary ml-2">{anzahl(zeilen.length, "Zuordnung", "Zuordnungen")}, Familien einmal gerechnet</span>
                </td>
                <td className={cn(LISTENFUSS, "text-body-sm text-right font-medium tabular-nums")}>{euro(jahr)}</td>
                <td colSpan={4} className={LISTENFUSS} />
              </tr>
            </tfoot>
          </Listentabelle>

          <Listenkoerper className="md:hidden">
            <ul className="space-y-2 p-3">
              {zeilen.map((z) => (
                <li key={z.id}>
                  <button
                    type="button"
                    onClick={() => setDialog(z)}
                    className={cn("bg-bg-primary border-border block w-full rounded-xl border px-4 py-3 text-left", z.luecken.length > 0 && "border-l-warning border-l-4")}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-body-sm text-text-primary truncate font-medium">{z.contactName}</span>
                      <span className="text-body-sm shrink-0 font-medium tabular-nums">{z.amount === 0 ? "frei" : euro(z.amount)}</span>
                    </span>
                    <span className="text-caption text-text-tertiary block">
                      {z.feeTypeName} · {PAYMENT_METHOD[z.paymentMethod]}
                      {z.payerId !== z.contactId ? ` · über ${z.householdName}` : ""}
                    </span>
                    {z.luecken.length > 0 && <span className="text-caption text-warning mt-1 block">{z.luecken[0]!.text}</span>}
                  </button>
                </li>
              ))}
            </ul>
            <div className={cn(LISTENFUSS, "flex justify-between")}>
              <span className="text-caption text-text-tertiary">{anzahl(zeilen.length, "Zuordnung", "Zuordnungen")}</span>
              <span className="text-body-sm font-medium tabular-nums">{euro(jahr)} im Jahr</span>
            </div>
          </Listenkoerper>
        </>
      )}

      {dialog && (
        <ZuordnungDialog
          zuordnung={dialog === "neu" ? null : dialog}
          arten={arten}
          mandate={mandate}
          kontakte={personen?.kontakte}
          haushalte={personen?.haushalte}
          datenbank={datenbank}
          onClose={() => setDialog(null)}
          onGespeichert={gespeichert}
        />
      )}
    </>
  );
}

function Hinweise({ z }: { z: ZuordnungView }) {
  if (z.luecken.length === 0 && !z.note && z.contactStatus === "ACTIVE") return <span className="text-text-tertiary">–</span>;
  return (
    <span className="flex flex-col gap-1">
      {z.contactStatus !== "ACTIVE" && (
        <span>
          <ContactStatusBadge status={z.contactStatus} />
        </span>
      )}
      {z.luecken.map((l) => (
        <span key={l.art} className="text-caption text-warning flex items-start gap-1">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate" title={l.text}>
            {l.text}
          </span>
        </span>
      ))}
      {z.note && (
        <span className="text-caption text-text-secondary truncate" title={z.note}>
          <TonBadge ton="info" className="mr-1">Sonderbetrag</TonBadge>
          {z.note}
        </span>
      )}
    </span>
  );
}
